/**
 * StudioSidebar — left navigation rail for /studio.
 *
 * Replaces the top action-button-row pattern with a vertical sidebar that
 * matches the iter-177 layout reference. Each nav item opens an existing
 * modal/drawer (no new routes — fastest path). Bottom of the sidebar has
 * a live Engine Status panel showing tempo, key, mode, sample rate, and
 * latency.
 *
 * Existing modal triggers wired:
 *   Studio        → scrolls to top
 *   Projects      → save sessions list
 *   Sessions      → save current session
 *   My Songs      → SongLibrary
 *   Recordings    → Recordings modal
 *   Backings      → Backing Vocals modal
 *   Presets       → JSFX preset menu (scrolls to plugins, opens picker)
 *   Templates     → Make Music modal → Templates dropdown
 *   Plugins       → Plugin Browser
 *   Explore       → Explore page
 *   Account       → Pricing modal (sign-in / upgrade flow)
 *   Settings      → Settings page
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Home, Folder, Disc3, Music2, Mic2, Layers, Sliders, Star,
  FileBox, UserCircle2, Settings as Cog,
  Cpu, Activity, FilePlus, Music4, Music, BookOpenText, Wand2, Repeat, AudioLines, Drum, Save, Download, Share2, Compass,
} from "lucide-react";
import { getUserToken } from "@/lib/userToken";
import { fetchRecordingStats } from "@/lib/recordingStats";
import NowPlayingPill from "@/components/daw/NowPlayingPill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sep 2026 — the Advanced workspace is organised into four plain groups
 * (Create · Record · Edit · Finish) plus Library and Account, so a first-time
 * visitor can find a tool by what they want to do. Every entry still opens the
 * same existing modal/drawer through `actions`.
 */
const NAV_GROUPS = [
  { id: "top", label: null, items: [
    { id: "studio",      label: "Studio",        icon: Home,     action: "scrollTop" },
    { id: "new-project", label: "New Project",   icon: FilePlus, action: "newProject", highlight: true },
  ] },
  { id: "create", label: "Create", hint: "Start a song", items: [
    { id: "make-music", label: "Make Music",    icon: Music4,       action: "openSuno" },
    { id: "compose",    label: "Compose",       icon: Music,        action: "openCompose" },
    { id: "lyrics",     label: "Lyrics Writer", icon: BookOpenText, action: "openLyrics" },
    { id: "jam",        label: "Jam Wizard",    icon: Wand2,        action: "openJam" },
    { id: "templates",  label: "Templates",     icon: Star,         action: "openTemplates" },
  ] },
  { id: "record", label: "Record", hint: "Your voice & takes", items: [
    { id: "recordings",  label: "Recordings",    icon: Mic2,   action: "openRecordings", badge: "recordingsCount" },
    { id: "backings",    label: "Backing Vocals", icon: Layers, action: "openBackingVocals" },
    { id: "loopstation", label: "Loop Station",  icon: Repeat, action: "openLoopStation" },
  ] },
  { id: "edit", label: "Edit", hint: "Shape the sound", items: [
    { id: "audio-editor", label: "Audio Editor", icon: AudioLines, action: "openMtEditor" },
    { id: "drums",        label: "Drum Machine", icon: Drum,       action: "openDrum" },
    { id: "bass",         label: "Bass Player",  icon: Music2,     action: "openBass" },
    { id: "loops",        label: "Loops",        icon: Disc3,      action: "openLoops" },
    { id: "presets",      label: "Effect Presets", icon: Sliders,  action: "openPresets" },
    { id: "plugins",      label: "Plugins",      icon: FileBox,    action: "openPlugins" },
    { id: "midi",         label: "MIDI",         icon: Activity,   action: "openMidi" },
  ] },
  { id: "finish", label: "Finish", hint: "Save, export, share", items: [
    { id: "save",     label: "Save Session",  icon: Save,     action: "saveSession" },
    { id: "sessions", label: "Sessions",      icon: Folder,   action: "openSavedSessions" },
    { id: "export",   label: "Export WAV",    icon: Download, action: "exportWav" },
    { id: "share",    label: "Share Session", icon: Share2,   action: "openShare" },
  ] },
  { id: "library", label: "Library", items: [
    { id: "projects", label: "Projects", icon: Folder,   action: "openLibrary" },
    { id: "songs",    label: "My Songs", icon: Music2,   action: "openLibrary", badge: "songsCount" },
    { id: "explore",  label: "Explore",  icon: Compass,  action: "openExplore" },
  ] },
  { id: "account", label: "Account", items: [
    { id: "account",  label: "Account & Pro", icon: UserCircle2, action: "openAccount" },
    { id: "settings", label: "Settings",      icon: Cog,         action: "openSettings" },
  ] },
];

const COLLAPSE_KEY = "elpm:sidebar:collapsed";

function readCollapsed() {
  try { return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "[]")); } catch (e) { void e; return new Set(); }
}

export default function StudioSidebar({
  activeId = "studio",
  engineStatus = "Ready",
  engineCtx,  // iter-179 — optional AudioContext reference for live state updates
  tempo,
  songKey,
  mode = "Dry",
  sampleRate,
  latencyMs,
  songsCount: songsCountProp,
  recordingsCount: recordingsCountProp,
  // iter-180 — Now Playing pill
  channels = [],
  selectedChannel = 0,
  playing = false,
  maxDur = 0,
  playStartedAt = 0,
  onJumpToChannel,
  onSeek,  // iter-181 — scrub-and-seek
  onTogglePlay,  // iter-183 — inline play/pause
  actions = {},  // { scrollTop, openLibrary, openRecordings, ... }
}) {
  const navigate = useNavigate();

  // iter-178 — live badge counts. Fetched once on mount (cached for 60s in
  // localStorage to avoid spamming the API on every studio reload) and
  // refreshed whenever the user opens the library/recordings modal.
  const [liveCounts, setLiveCounts] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("elpm:sidebar:counts") || "null");
      if (cached && Date.now() - cached.t < 60_000) return cached.v;
    } catch (e) { void e; }
    return { songs: 0, recordings: 0 };
  });

  const fetchCounts = useCallback(async () => {
    const userToken = getUserToken();
    try {
      const [s, r] = await Promise.allSettled([
        axios.get(`${API}/library/songs`, { params: { user_token: userToken, limit: 200 }, withCredentials: true, timeout: 8_000 }),
        fetchRecordingStats(),
      ]);
      const songs = s.status === "fulfilled" ? (Array.isArray(s.value.data) ? s.value.data.length : 0) : 0;
      // Cloud badge = total stored TAKES (per-channel files), not sessions.
      const recordings = r.status === "fulfilled" ? r.value.takes : 0;
      const v = { songs, recordings };
      setLiveCounts(v);
      try { localStorage.setItem("elpm:sidebar:counts", JSON.stringify({ v, t: Date.now() })); } catch (e) { void e; }
    } catch (e) { void e; }
  }, []);

  useEffect(() => {
    fetchCounts();
    // Refresh when other components fire these events.
    const onUpdate = () => fetchCounts();
    window.addEventListener("elpm:library-updated", onUpdate);
    window.addEventListener("elpm:recordings-updated", onUpdate);
    return () => {
      window.removeEventListener("elpm:library-updated", onUpdate);
      window.removeEventListener("elpm:recordings-updated", onUpdate);
    };
  }, [fetchCounts]);

  // iter-179 — Subscribe to AudioContext 'statechange' so the indicator
  // flips between green/amber/rose the moment the engine state changes,
  // without depending on the parent re-rendering. Polling fallback every
  // 500ms covers browsers that don't reliably fire 'statechange' (e.g.
  // when other tabs steal audio focus).
  const [liveEngineStatus, setLiveEngineStatus] = useState(engineStatus);
  useEffect(() => {
    setLiveEngineStatus(engineStatus);
  }, [engineStatus]);
  useEffect(() => {
    if (!engineCtx) return;
    const compute = () => {
      const s = engineCtx.state;
      const label = s === "running" ? "Ready" : s === "suspended" ? "Suspended" : "Closed";
      setLiveEngineStatus(label);
    };
    compute();
    let onChange;
    try {
      onChange = () => compute();
      engineCtx.addEventListener?.("statechange", onChange);
    } catch (e) { void e; }
    // Belt-and-braces polling — 500ms.
    const id = setInterval(compute, 500);
    return () => {
      try { engineCtx.removeEventListener?.("statechange", onChange); } catch (e) { void e; }
      clearInterval(id);
    };
  }, [engineCtx]);
  const displayedStatus = liveEngineStatus;

  const songsCount = songsCountProp ?? liveCounts.songs;
  const recordingsCount = recordingsCountProp ?? liveCounts.recordings;
  const counts = { songsCount, recordingsCount };

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleGroup = (gid) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch (e) { void e; }
      return next;
    });
  };

  const handleClick = (item) => {
    const handler = actions[item.action];
    if (typeof handler === "function") {
      handler();
    } else if (item.action === "scrollTop") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (item.action === "openExplore") {
      navigate("/explore");
    }
  };

  const renderItem = (item) => {
    const Icon = item.icon;
    const isActive = item.id === activeId;
    const badgeValue = item.badge ? counts[item.badge] : 0;
    const badgeLabel = badgeValue > 99 ? "99+" : String(badgeValue);
    const badgeTitle = item.badge === "recordingsCount" && badgeValue > 0
      ? `${badgeValue} take${badgeValue === 1 ? "" : "s"} stored in the cloud · click to reload any into a channel`
      : undefined;
    return (
      <li key={item.id}>
        <button
          type="button"
          data-testid={`sidebar-nav-${item.id}`}
          title={badgeTitle}
          onClick={() => handleClick(item)}
          className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition ${
            isActive
              ? "bg-gradient-to-r from-violet-500/30 via-violet-500/10 to-transparent text-white shadow-[0_4px_18px_-8px_rgba(167,139,250,0.65)] ring-1 ring-violet-400/30"
              : item.highlight
              ? "bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-transparent text-emerald-50 ring-1 ring-emerald-400/25 hover:from-emerald-500/25 hover:via-emerald-500/10 hover:ring-emerald-300/40"
              : "text-white/80 hover:bg-white/[0.06] hover:text-white"
          }`}
        >
          <Icon className={`h-4 w-4 flex-shrink-0 ${
            isActive ? "text-violet-200"
            : item.highlight ? "text-emerald-300 group-hover:text-emerald-200"
            : "text-white/60 group-hover:text-white/90"
          }`} />
          <span className="font-display text-[13.5px] font-medium leading-none">{item.label}</span>
          {item.badge && badgeValue > 0 && (
            <span
              data-testid={`sidebar-badge-${item.id}`}
              className={`ml-auto rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider tabular-nums ${
                isActive
                  ? "bg-violet-500/40 text-violet-50 ring-1 ring-violet-300/40"
                  : "bg-fuchsia-500/25 text-fuchsia-100 ring-1 ring-fuchsia-400/30"
              }`}
            >
              {badgeLabel}
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <aside
      data-testid="studio-sidebar"
      className="hidden lg:flex w-[210px] xl:w-[232px] flex-shrink-0 flex-col gap-4 sticky top-4 self-start max-h-[calc(100vh-2rem)]"
    >
      {/* Logo */}
      <Link to="/" data-testid="sidebar-logo" className="flex items-center justify-center rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900/80 to-zinc-950/95 px-4 py-5 transition hover:border-violet-400/30">
        <div className="flex flex-col items-center gap-1">
          <span className="font-display text-2xl font-bold tracking-wider text-white">ELPM</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">Advanced Studio</span>
        </div>
      </Link>

      {/* Nav — grouped Create · Record · Edit · Finish */}
      <nav data-testid="sidebar-groups" className="flex-1 overflow-y-auto rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900/60 to-zinc-950/80 p-2 backdrop-blur-sm">
        {NAV_GROUPS.map((group) => {
          const isCollapsed = group.label && collapsed.has(group.id);
          return (
            <div key={group.id} data-testid={`sidebar-group-${group.id}`} className="mb-1.5 last:mb-0">
              {group.label ? (
                <button
                  type="button"
                  data-testid={`sidebar-group-toggle-${group.id}`}
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleGroup(group.id)}
                  title={group.hint}
                  className="mt-1 flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left hover:bg-white/[0.04]"
                >
                  <span className="font-display text-[11.5px] font-bold uppercase tracking-[0.2em] text-fuchsia-200/90">{group.label}</span>
                  <span className={`font-mono text-[10px] text-white/50 transition-transform ${isCollapsed ? "" : "rotate-90"}`}>›</span>
                </button>
              ) : null}
              {!isCollapsed ? <ul className="space-y-0.5">{group.items.map(renderItem)}</ul> : null}
            </div>
          );
        })}
      </nav>

      {/* iter-180 — Now Playing pill (Ableton-style). Hides automatically
          when no channels have audio loaded. Click → jumps to that channel. */}
      <NowPlayingPill
        channels={channels}
        selectedChannel={selectedChannel}
        playing={playing}
        ctx={engineCtx}
        maxDur={maxDur}
        playStartedAt={playStartedAt}
        onJumpToChannel={onJumpToChannel}
        onSeek={onSeek}
        onTogglePlay={onTogglePlay}
      />

      {/* Engine Status panel */}
      <div
        data-testid="sidebar-engine-status"
        className="rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900/60 to-zinc-950/85 p-4"
      >
        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">
          <Cpu className="h-3 w-3" />
          Engine Status
        </div>

        {/* Ready indicator — color-coded by AudioContext state */}
        <div className="mb-3 flex items-center gap-2">
          <span className="relative grid h-2 w-2 place-items-center">
            <span className={`absolute inset-0 animate-ping rounded-full ${
              displayedStatus === "Ready" ? "bg-emerald-500/50"
              : displayedStatus === "Suspended" ? "bg-amber-500/50"
              : "bg-rose-500/50"
            }`} />
            <span className={`relative h-2 w-2 rounded-full ${
              displayedStatus === "Ready" ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]"
              : displayedStatus === "Suspended" ? "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.7)]"
              : "bg-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.7)]"
            }`} />
          </span>
          <span className={`font-display text-[13px] font-semibold ${
            displayedStatus === "Ready" ? "text-emerald-200"
            : displayedStatus === "Suspended" ? "text-amber-200"
            : "text-rose-200"
          }`}>{displayedStatus}</span>
        </div>

        {/* Stats list */}
        <dl className="space-y-2">
          <StatusRow label="Tempo"       value={tempo != null ? `${tempo} BPM` : "—"} testid="engine-stat-tempo" />
          <StatusRow label="Key"         value={songKey || "—"}                       testid="engine-stat-key" />
          <StatusRow label="Mode"        value={mode || "—"}                          testid="engine-stat-mode" />
          <StatusRow label="Sample Rate" value={sampleRate ? `${Math.round(sampleRate/1000)} kHz` : "—"} testid="engine-stat-rate" />
          <StatusRow label="Latency"     value={latencyMs != null ? `Low (${latencyMs} ms)` : "—"} testid="engine-stat-latency" highlight />
        </dl>
      </div>

      {/* Spacer */}
      <div className="mt-auto" />
    </aside>
  );
}

function StatusRow({ label, value, testid, highlight = false }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-white/[0.04] pb-1.5 last:border-0 last:pb-0">
      <dt className="font-mono text-[9px] uppercase tracking-wider text-white/40">{label}</dt>
      <dd data-testid={testid} className={`font-display text-[11px] font-semibold tabular-nums ${highlight ? "text-emerald-300" : "text-white/85"}`}>{value}</dd>
    </div>
  );
}

// Tiny helper export for hosts that need to integrate with the engine without
// digging into props: returns a usable AudioContext sample rate if one exists.
export function useSampleRateLatency() {
  const [info, setInfo] = useState({ sampleRate: undefined, latencyMs: 32 });
  useEffect(() => {
    try {
      const ctx = window?.audioCtx || (window?.audioEngineRef?.current?.context);
      if (ctx?.sampleRate) {
        const lat = ctx.baseLatency ? Math.round(ctx.baseLatency * 1000) : 32;
        setInfo({ sampleRate: ctx.sampleRate, latencyMs: lat });
      }
    } catch (e) { void e; }
  }, []);
  return info;
}

// Indirectly useful so consumers don't have to import lucide directly.
export const SidebarIcons = { Cpu, Activity };
